import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useMainContext } from '../context/MainContext.tsx';
import { useMergedConfig } from '../context/MergedConfigProvider';
import { useMParticle } from '../hooks/useMparticle.ts';
import { IdentityStatus } from '../onboarding-api';
import { REGIONS, getRegionForCountry } from '../utils/countriesConfig';
import { getDefaultLanguageForRegion, getDemoSignupJourney } from '../utils/demoSignupJourneyMap.ts';
import { useIsMobile } from '../utils/useIsMobile.ts';
import { EmailScreen } from './signup/EmailScreen.tsx';
import { OtpScreen } from './signup/OtpScreen.tsx';
import { PasswordScreen } from './signup/PasswordScreen.tsx';
import { RegionSelectScreen } from './signup/RegionSelectScreen.tsx';

enum SIGNUP_STEP {
  REGION = 'REGION',
  EMAIL = 'EMAIL',
  PASSWORD = 'PASSWORD',
  OTP = 'OTP',
}

export type SignUpState = {
  email: string;
  emailStatus?: IdentityStatus;
  password: string;
  requiresSetPassword?: boolean;
  requiresOtp?: boolean;
  otpVerified?: boolean;
  oneTimeToken?: string;
  csrfToken: string;
  connectApplicationId?: string;
  isRegionUnknown?: boolean;
};

const INITIAL_STATE: SignUpState = {
  email: '',
  emailStatus: undefined,
  password: '',
  requiresSetPassword: undefined,
  requiresOtp: undefined,
  otpVerified: undefined,
  oneTimeToken: undefined,
  csrfToken: '',
  isRegionUnknown: true,
};

type Props = {
  onCompleted: (oneTimeToken: string, connectApplicationId?: string, email?: string, password?: string) => void;
};

export const SignUpJourney: FC<Props> = ({ onCompleted }) => {
  const [signUpState, setSignUpState] = useState<SignUpState>(INITIAL_STATE);
  const { setUserData } = useMParticle();
  const { setCsrfToken } = useMainContext();
  const { lang, isRegionUnknown, pegaStartingFields, region } = useMergedConfig();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const showRegionSelect = isMobile || isRegionUnknown; // Always show region select for mobile, and for desktop if the region is unknown

  const redirectToDemoSignup = useCallback(
    (commercialRegion: string) => {
      const effectiveLang = getDemoSignupJourney(commercialRegion, lang, isMobile)
        ? lang
        : getDefaultLanguageForRegion(commercialRegion, isMobile);
      const selectedJid = getDemoSignupJourney(commercialRegion, effectiveLang, isMobile);
      window.location.href = `${import.meta.env.VITE_CMC_REDIRECTION_TO_DEMO_URL}/#?l=${effectiveLang}&r=${commercialRegion}${selectedJid ? `&jid=${selectedJid}` : ''}${isMobile ? '&isGateway=true&inApp=1' : ''}`;
    },
    [lang, isMobile]
  );

  const hasCheckedPreResolvedRegionRef = useRef(false);
  useEffect(() => {
    if (hasCheckedPreResolvedRegionRef.current || region !== '' || isRegionUnknown) return;
    hasCheckedPreResolvedRegionRef.current = true;

    const onboardingRegion = pegaStartingFields.OnboardingRegion;
    if (!onboardingRegion) return;

    const commercialRegion = getRegionForCountry(onboardingRegion, lang);
    if (commercialRegion !== REGIONS.UK) {
      redirectToDemoSignup(commercialRegion);
    }
  }, [region, isRegionUnknown, pegaStartingFields.OnboardingRegion, lang, redirectToDemoSignup]);

  const persistSelectedCountry = (countryCode: string) => {
    const params = new URLSearchParams(location.search);
    params.set('region', countryCode.toUpperCase());
    // Delete any incoming language when UK is the selected region
    params.delete('language');
    params.set('l', 'en');
    history.replace({ search: params.toString() });
  };

  const clearSelectedCountry = () => {
    const params = new URLSearchParams(location.search);
    params.delete('region');
    history.replace({ search: params.toString() });
  };
  const step = useMemo(() => {
    if (isRegionUnknown && signUpState.isRegionUnknown !== false) {
      return SIGNUP_STEP.REGION;
    }
    if (!signUpState.emailStatus) {
      return SIGNUP_STEP.EMAIL;
    }
    if (signUpState.requiresOtp && !signUpState.otpVerified) {
      return SIGNUP_STEP.OTP;
    }
    if (!signUpState.password) {
      return SIGNUP_STEP.PASSWORD;
    }
  }, [signUpState, isRegionUnknown]);

  useEffect(() => {
    if (signUpState.oneTimeToken) {
      setCsrfToken(signUpState.csrfToken);

      setUserData({
        email: signUpState.email,
      });

      onCompleted(signUpState.oneTimeToken, signUpState.connectApplicationId, signUpState.email, signUpState.password);
    }
  }, [signUpState, onCompleted, setUserData, setCsrfToken]);
  return (
    <>
      {step === SIGNUP_STEP.REGION && showRegionSelect && (
        <RegionSelectScreen
          lang={lang}
          onCountrySelected={(country) => {
            const commercialRegion = getRegionForCountry(country.value, lang);
            if (commercialRegion !== REGIONS.UK) {
              redirectToDemoSignup(commercialRegion);
              return;
            }

            persistSelectedCountry(country.value);
            setSignUpState((currentState) => ({
              ...currentState,
              isRegionUnknown: false,
            }));
          }}
        />
      )}
      {step === SIGNUP_STEP.EMAIL && (
        <EmailScreen
          signUpState={signUpState}
          onCompleted={(email, status, csrfToken) => {
            setSignUpState((currentState) => {
              return {
                ...currentState,
                email,
                emailStatus: status,
                csrfToken,
              };
            });
          }}
          onGoBack={
            isRegionUnknown || signUpState.isRegionUnknown === false
              ? () => {
                  clearSelectedCountry();
                  setSignUpState((currentState) => ({
                    ...INITIAL_STATE,
                    email: currentState.email,
                  }));
                }
              : undefined
          }
        />
      )}
      {step === SIGNUP_STEP.PASSWORD && (
        <PasswordScreen
          signUpState={signUpState}
          onGoBack={() => {
            setSignUpState((currentState) => ({
              ...INITIAL_STATE,
              email: currentState.email,
            }));
          }}
          onForgotPassword={(requiresOtp: boolean) => {
            setSignUpState((currentState) => ({
              ...currentState,
              password: '',
              requiresOtp,
            }));
          }}
          onCompleted={(password: string, requiresOtp: boolean, oneTimeToken?: string) => {
            setSignUpState((currentState) => ({
              ...currentState,
              password,
              requiresOtp,
              oneTimeToken,
            }));
          }}
        />
      )}
      {step === SIGNUP_STEP.OTP && (
        <OtpScreen
          signUpState={signUpState}
          onGoBack={() => {
            setSignUpState((currentState) => ({
              ...INITIAL_STATE,
              email: currentState.email,
            }));
          }}
          onCompleted={(requiresSetPassword: boolean, oneTimeToken?: string, connectApplicationId?: string) => {
            setSignUpState((currentState) => ({
              ...currentState,
              otpVerified: true,
              requiresSetPassword,
              oneTimeToken,
              connectApplicationId,
            }));
          }}
        />
      )}
    </>
  );
};
